// src/store/slices/equivalentFractionsSlice.ts
/**
 * Equivalent Fractions view mode state with localStorage-backed defaults.
 * Persists the preferred list of denominators, while keeping a
 * session-only active list that can be edited without changing preferences.
 */

import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { EquivalentFractionsState, ViewMode, RootState } from '../../types/redux';
import { centerOrigin } from './viewportSlice';

const STORAGE_KEY = 'nl:equivalentFractions';
const MAX_DENOMINATORS = 12;
const DEFAULT_DENOMINATORS = [2, 3, 4, 6, 12] as const;

const defaultState: EquivalentFractionsState = {
  mode: 'standard',
  denominators: [...DEFAULT_DENOMINATORS],
  activeDenominators: [...DEFAULT_DENOMINATORS],
  integerLabelsOnly: true,
  labelFormat: 'fraction',
};

function normalizeDenominators(input: number[] | undefined, fallback = DEFAULT_DENOMINATORS): number[] {
  if (!Array.isArray(input)) return [...fallback];
  const uniqueSorted = Array.from(
    new Set(
      input
        .map((d) => Math.abs(Math.trunc(d)))
        .filter((d) => Number.isFinite(d) && d > 0)
    )
  )
    .sort((a, b) => a - b)
    .slice(0, MAX_DENOMINATORS);
  return uniqueSorted.length > 0 ? uniqueSorted : [...fallback];
}

function loadFromStorage(): EquivalentFractionsState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState;
    const parsed = JSON.parse(raw);
    const persistedDenominators = normalizeDenominators(parsed.denominators);
    const state: EquivalentFractionsState = {
      mode: parsed.mode === 'equivalentFractions' 
        ? 'equivalentFractions' 
        : parsed.mode === 'coordinateGrid'
          ? 'coordinateGrid'
          : 'standard',
      denominators: persistedDenominators,
      activeDenominators: normalizeDenominators(parsed.activeDenominators, persistedDenominators),
      integerLabelsOnly: parsed.integerLabelsOnly !== false,
      labelFormat: parsed.labelFormat === 'decimal' ? 'decimal' : 'fraction',
    };
    return state;
  } catch (error) {
    console.warn('Failed to load equivalent fractions state from storage', error);
    return defaultState;
  }
}

function saveToStorage(state: EquivalentFractionsState) {
  try {
    const { mode, denominators, integerLabelsOnly, labelFormat } = state;
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        mode,
        denominators,
        integerLabelsOnly,
        labelFormat,
      })
    );
  } catch (error) {
    console.warn('Failed to save equivalent fractions state', error);
  }
}

const initialState: EquivalentFractionsState = (() => {
  const loaded = loadFromStorage();
  const activeDenominators = normalizeDenominators(loaded.activeDenominators, loaded.denominators);
  return {
    ...loaded,
    activeDenominators,
  };
})();

export const equivalentFractionsSlice = createSlice({
  name: 'equivalentFractions',
  initialState,
  reducers: {
    setMode: (state, action: PayloadAction<ViewMode>) => {
      state.mode = action.payload;
      saveToStorage(state);
    },
    setDenominators: (state, action: PayloadAction<number[]>) => {
      const sanitized = normalizeDenominators(action.payload);
      state.denominators = sanitized;
      state.activeDenominators = sanitized;
      saveToStorage(state);
    },
    addDenominator: (state, action: PayloadAction<number>) => {
      const candidate = Math.abs(Math.trunc(action.payload));
      if (!candidate || !Number.isFinite(candidate)) return;
      const next = normalizeDenominators([...state.activeDenominators, candidate], state.denominators);
      state.activeDenominators = next;
    },
    removeDenominator: (state, action: PayloadAction<number>) => {
      const remaining = state.activeDenominators.filter((d) => d !== action.payload);
      state.activeDenominators = remaining.length > 0 ? remaining : [...state.denominators];
    },
    resetActiveDenominators: (state) => {
      state.activeDenominators = [...state.denominators];
    },
    setIntegerLabelsOnly: (state, action: PayloadAction<boolean>) => {
      state.integerLabelsOnly = action.payload;
      saveToStorage(state);
    },
    setLabelFormat: (state, action: PayloadAction<'fraction' | 'decimal'>) => {
      state.labelFormat = action.payload;
      saveToStorage(state);
    },
    loadEquivalentFractionsState: (state, action: PayloadAction<EquivalentFractionsState>) => {
      const sanitizedBase = normalizeDenominators(action.payload.denominators);
      const sanitizedActive = normalizeDenominators(
        action.payload.activeDenominators ?? action.payload.denominators,
        sanitizedBase
      );
      state.mode = action.payload.mode;
      state.denominators = sanitizedBase;
      state.activeDenominators = sanitizedActive;
      state.integerLabelsOnly = action.payload.integerLabelsOnly;
      state.labelFormat = action.payload.labelFormat ?? 'fraction';
      saveToStorage(state);
    },
  },
});

export const {
  setMode,
  setDenominators,
  addDenominator,
  removeDenominator,
  resetActiveDenominators,
  setIntegerLabelsOnly,
  setLabelFormat,
  loadEquivalentFractionsState,
} = equivalentFractionsSlice.actions;

/**
 * Async thunk to set view mode with coordinate grid initialization
 * When switching to coordinate grid mode, centers the origin in the viewport
 */
export const setViewMode = createAsyncThunk<
  ViewMode,
  { mode: ViewMode; viewportWidth?: number; viewportHeight?: number },
  { state: RootState }
>(
  'equivalentFractions/setViewMode',
  async ({ mode, viewportWidth, viewportHeight }, { dispatch, getState }) => {
    // Set the mode first
    dispatch(setMode(mode));
    
    // If switching to coordinate grid mode, center the origin
    if (mode === 'coordinateGrid') {
      const width = viewportWidth ?? 800; // Default width
      const height = viewportHeight ?? 400; // Default height
      
      // Center the origin in the viewport
      dispatch(centerOrigin({ width, height }));
    }
    
    return mode;
  }
);

export default equivalentFractionsSlice.reducer;
